/* ==========================================================================
   VINOD wishlist — Expanse 10.0

   Saved on the visitor's own device in localStorage. No login, no app, no
   backend: the store keeps a list of product handles, and the wishlist page
   rehydrates each one from Shopify's own /products/<handle>.js endpoint.

   Hearts are INJECTED at runtime rather than added to the theme's card
   snippet, so collection grids, home page rows, search results, quick view
   and the product page are all covered without editing Expanse internals —
   and nothing breaks if the card markup changes in a theme update.

   On the product page the heart joins the buy row as a 52px icon square,
   sitting immediately before Add to Cart (placement 1a).
   ========================================================================== */
(function () {
  'use strict';

  var KEY = 'vinod:wishlist';
  var PAGE = '/pages/wishlist';
  var MAX = 200;

  /* ------------------------------------------------------------- the store */

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter(function (h) { return typeof h === 'string' && h; }) : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX)));
    } catch (e) {
      /* private mode or full quota — the session still works, it just
         will not be remembered next visit */
    }
    broadcast();
  }

  function has(handle) { return read().indexOf(handle) !== -1; }

  function add(handle) {
    var list = read();
    if (list.indexOf(handle) === -1) list.unshift(handle);
    write(list);
  }

  function remove(handle) {
    write(read().filter(function (h) { return h !== handle; }));
  }

  function toggle(handle) {
    if (has(handle)) { remove(handle); return false; }
    add(handle);
    return true;
  }

  function broadcast() {
    document.dispatchEvent(new CustomEvent('vinod:wishlist:change', {
      detail: { count: read().length, handles: read() }
    }));
  }

  // another tab changed the list
  window.addEventListener('storage', function (e) {
    if (e.key === KEY) { syncAll(); paintBadges(); }
  });

  /* ------------------------------------------------------------------ utils */

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function handleFromHref(href) {
    if (!href) return '';
    var marker = '/products/';
    var i = href.indexOf(marker);
    if (i === -1) return '';
    var rest = href.slice(i + marker.length);
    rest = rest.split('?')[0].split('#')[0].split('/')[0];
    return decodeURIComponent(rest);
  }

  function money(cents) {
    var value = (cents / 100).toFixed(2).replace(/\.00$/, '');
    var parts = value.split('.');
    var whole = parts[0];
    var out;
    // Indian grouping: last three digits, then pairs
    if (whole.length > 3) {
      var last3 = whole.slice(-3);
      var restDigits = whole.slice(0, -3);
      out = restDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
    } else {
      out = whole;
    }
    return '₹' + out + (parts[1] ? '.' + parts[1] : '');
  }

  var HEART = '' +
    '<svg class="vd-wl-btn__line" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8z"></path>' +
    '</svg>' +
    '<svg class="vd-wl-btn__fill" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">' +
      '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8z"></path>' +
    '</svg>';

  /* ------------------------------------------------------------------ toast */

  var toastEl = null;
  var toastTimer = null;

  function toast(text) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'vd-wl-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = text;
    toastEl.classList.add('is-shown');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove('is-shown');
    }, 1900);
  }

  /* ------------------------------------------------------------ heart button */

  function makeButton(handle, pdp) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'vd-wl-btn' + (pdp ? ' vd-wl-btn--pdp' : '');
    btn.setAttribute('data-vd-wl-btn', handle);
    btn.innerHTML = HEART;
    syncButton(btn);
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var saved = toggle(handle);
      syncAll();
      paintBadges();
      if (saved) {
        btn.classList.remove('is-bumped');
        void btn.offsetWidth;
        btn.classList.add('is-bumped');
      }
      toast(saved ? 'Saved to your wishlist' : 'Removed from your wishlist');
    });
    return btn;
  }

  function syncButton(btn) {
    var handle = btn.getAttribute('data-vd-wl-btn');
    var on = has(handle);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.setAttribute('aria-label', on ? 'Remove from wishlist' : 'Save to wishlist');
    btn.title = on ? 'Remove from wishlist' : 'Save to wishlist';
  }

  function syncAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-vd-wl-btn]'), syncButton);
  }

  /* --------------------------------------------------- inject onto the cards */

  // Walk up from a product link to something card-shaped.
  var CARD_HINTS = [
    'grid__item', 'grid-item', 'product-grid-item', 'grid-product',
    'product-card', 'card-product', 'productitem', 'product-item'
  ];

  function cardRoot(link) {
    var el = link;
    for (var i = 0; i < 8 && el && el !== document.body; i++) {
      if (el.hasAttribute && el.hasAttribute('data-product-handle')) return el;
      var cls = (el.className && typeof el.className === 'string') ? el.className.toLowerCase() : '';
      for (var j = 0; j < CARD_HINTS.length; j++) {
        if (cls.indexOf(CARD_HINTS[j]) !== -1) return el;
      }
      if (el.tagName === 'LI') return el;
      el = el.parentElement;
    }
    return null;
  }

  // Prefer to sit over the image, so the heart never covers the title or price.
  function mountTarget(card) {
    var media = card.querySelector(
      '.grid-product__image-mask, .grid-product__image-wrapper, .image-wrap, ' +
      '.product-grid-item__image, .card__media, .grid__image-ratio, picture, img'
    );
    if (!media) return card;
    var wrap = media.tagName === 'IMG' || media.tagName === 'PICTURE' ? media.parentElement : media;
    return wrap || card;
  }

  function decorateCards(scope) {
    var scopeEl = scope || document;
    var links = scopeEl.querySelectorAll('a[href*="/products/"]');

    Array.prototype.forEach.call(links, function (link) {
      // never inside our own wishlist grid
      if (link.closest('[data-vd-wl-grid]')) return;
      if (link.closest('.vd-wl-card')) return;

      var handle = handleFromHref(link.getAttribute('href'));
      if (!handle) return;

      var card = cardRoot(link);
      if (!card) return;
      if (card.getAttribute('data-vd-wl-done') === '1') return;

      // a card must actually show an image; this filters out plain text links
      if (!card.querySelector('img')) return;

      card.setAttribute('data-vd-wl-done', '1');

      var target = mountTarget(card);
      target.classList.add('vd-wl-host');
      target.appendChild(makeButton(handle, false));
    });
  }

  /* ------------------------------------------------------ inject onto the PDP */

  // Find the flex/grid row that actually holds the Add to Cart button, so the
  // heart becomes a sibling in that row rather than a full-width bar beneath it.
  function buyRowFor(addBtn) {
    var el = addBtn.parentElement;
    for (var i = 0; i < 6 && el && el !== document.body; i++) {
      var display = '';
      try { display = getComputedStyle(el).display; } catch (e) {}
      if (display === 'flex' || display === 'grid') return el;
      el = el.parentElement;
    }
    return null;
  }

  // Which direct child of `row` contains `node`
  function childOf(row, node) {
    var el = node;
    while (el && el.parentElement !== row) el = el.parentElement;
    return el;
  }

  function decoratePdp() {
    if (document.body.className.indexOf('template-product') === -1) return;
    if (document.querySelector('.vd-wl-btn--pdp')) return;

    var handle = handleFromHref(window.location.pathname);
    if (!handle) return;

    var addBtn =
      document.querySelector('form[action*="/cart/add"] [type="submit"]') ||
      document.querySelector('form[action*="/cart/add"] button') ||
      document.querySelector('[data-add-to-cart]') ||
      document.querySelector('.add-to-cart');

    if (addBtn) {
      var row = buyRowFor(addBtn);
      if (row) {
        var anchor = childOf(row, addBtn);
        if (anchor) {
          row.insertBefore(makeButton(handle, true), anchor);
          return;
        }
      }
      // row is not a flex container — sit inline, immediately before the button
      var loose = makeButton(handle, true);
      loose.classList.add('vd-wl-btn--loose');
      addBtn.parentNode.insertBefore(loose, addBtn);
      return;
    }

    // no recognisable button (fully sold out, or a custom block) — fall back
    var block =
      document.querySelector('.product-block--buy-buttons') ||
      document.querySelector('block-product-buy-buttons') ||
      document.querySelector('[data-product-form]');

    if (!block) return;

    var fallback = makeButton(handle, true);
    fallback.classList.add('vd-wl-btn--loose');
    block.parentNode.insertBefore(fallback, block.nextSibling);
  }

  /* --------------------------------------------------------------- badges */

  function paintBadges() {
    var count = read().length;

    var links = document.querySelectorAll('a[href$="/pages/wishlist"], a[href*="/pages/wishlist?"]');
    Array.prototype.forEach.call(links, function (link) {
      link.classList.add('vd-wl-linked');

      var badge = link.querySelector('.vd-wl-badge');
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'vd-wl-badge';
        // the desktop header stacks icons inline; the mobile bar centres them
        if (!link.classList.contains('vd-mobile-nav__item')) {
          badge.className += ' vd-wl-badge--inline';
        }
        link.appendChild(badge);
      }

      badge.textContent = count;
      if (count > 0) badge.removeAttribute('hidden');
      else badge.setAttribute('hidden', 'hidden');
    });
  }

  /* ------------------------------------------------------------ open the cart */

  // After adding from the wishlist, show the cart the way the rest of the site
  // does: refresh the ajax cart, then open the drawer. If no drawer trigger can
  // be found, go to the cart page rather than leaving the tap looking ignored.
  function revealCart() {
    document.dispatchEvent(new CustomEvent('cart:build'));

    var toggle =
      document.querySelector('[data-cart-toggle]') ||
      document.querySelector('[data-action="toggle-cart"]') ||
      document.querySelector('button[aria-controls="CartDrawer"]') ||
      document.querySelector('.site-header__cart') ||
      document.querySelector('header-toggle-cart button') ||
      document.querySelector('header-toggle-cart');

    if (toggle && typeof toggle.click === 'function') {
      toggle.click();
      return;
    }

    window.location.href = root() + 'cart';
  }

  /* --------------------------------------------------------- wishlist page */

  function countText(n) {
    return n === 1 ? '1 item saved on this device.' : n + ' items saved on this device.';
  }

  function renderPage() {
    var page = document.querySelector('[data-vd-wl-page]');
    if (!page) return;

    var grid = page.querySelector('[data-vd-wl-grid]');
    var empty = page.querySelector('[data-vd-wl-empty]');
    var loading = page.querySelector('[data-vd-wl-loading]');
    var sub = page.querySelector('[data-vd-wl-sub]');
    var handles = read();

    function done() { if (loading) loading.setAttribute('hidden', 'hidden'); }

    function showEmpty() {
      grid.setAttribute('hidden', 'hidden');
      empty.removeAttribute('hidden');
      if (sub) sub.textContent = '';
    }

    if (!handles.length) {
      done();
      showEmpty();
      return;
    }

    Promise.all(handles.map(function (handle) {
      return fetch(root() + 'products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      done();

      var live = products.filter(Boolean);

      // drop anything that no longer exists, so the list self-heals
      var alive = live.map(function (p) { return p.handle; });
      if (alive.length !== handles.length) {
        write(handles.filter(function (h) { return alive.indexOf(h) !== -1; }));
      }

      if (!live.length) {
        showEmpty();
        return;
      }

      empty.setAttribute('hidden', 'hidden');
      grid.removeAttribute('hidden');
      grid.innerHTML = '';
      live.forEach(function (p) { grid.appendChild(card(p)); });

      if (sub) sub.textContent = countText(live.length);
    });
  }

  function card(p) {
    var variant = null;
    for (var i = 0; i < p.variants.length; i++) {
      if (p.variants[i].available) { variant = p.variants[i]; break; }
    }

    var url = root() + 'products/' + p.handle;
    var img = p.featured_image || (p.images && p.images[0]) || '';
    var price = variant ? variant.price : p.price;
    var was = variant ? variant.compare_at_price : p.compare_at_price;
    var onSale = was && was > price;

    var el = document.createElement('article');
    el.className = 'vd-wl-card';

    el.innerHTML = '' +
      '<a class="vd-wl-card__media" href="' + url + '">' +
        (img ? '<img src="' + img + '&width=600" alt="" loading="lazy">' : '') +
      '</a>' +
      '<div class="vd-wl-card__body">' +
        '<a class="vd-wl-card__title" href="' + url + '">' + p.title + '</a>' +
        '<div class="vd-wl-card__price">' +
          '<span>' + money(price) + '</span>' +
          (onSale ? '<span class="vd-wl-card__was">' + money(was) + '</span>' : '') +
          (onSale ? '<span class="vd-wl-card__save">Save ' + money(was - price) + '</span>' : '') +
        '</div>' +
        '<div class="vd-wl-card__actions">' +
          (variant
            ? '<button type="button" class="vd-wl-card__add" data-variant="' + variant.id + '">Add to Cart</button>'
            : '<span class="vd-wl-card__sold">Sold out</span>') +
          '<button type="button" class="vd-wl-card__remove" aria-label="Remove from wishlist" title="Remove">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
              'stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"></path></svg>' +
          '</button>' +
        '</div>' +
      '</div>';

    var addBtn = el.querySelector('.vd-wl-card__add');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        addBtn.disabled = true;
        var original = addBtn.textContent;
        addBtn.textContent = 'Adding…';

        fetch(root() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(addBtn.dataset.variant), quantity: 1 }] })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('add failed');
            return r.json();
          })
          .then(function () {
            addBtn.classList.add('is-done');
            addBtn.textContent = 'Added';
            toast('Added to your cart');
            revealCart();
            setTimeout(function () {
              addBtn.classList.remove('is-done');
              addBtn.textContent = original;
              addBtn.disabled = false;
            }, 1800);
          })
          .catch(function () {
            addBtn.textContent = original;
            addBtn.disabled = false;
            toast('Could not add that — please try again');
          });
      });
    }

    el.querySelector('.vd-wl-card__remove').addEventListener('click', function () {
      remove(p.handle);
      el.remove();
      paintBadges();
      syncAll();

      var left = read().length;
      if (!left) {
        renderPage();
      } else {
        var page = document.querySelector('[data-vd-wl-page]');
        var sub = page && page.querySelector('[data-vd-wl-sub]');
        if (sub) sub.textContent = countText(left);
      }
      toast('Removed from your wishlist');
    });

    return el;
  }

  /* ------------------------------------------------------------- bootstrap */

  function run(scope) {
    decorateCards(scope);
    decoratePdp();
    paintBadges();
  }

  function boot() {
    run();
    renderPage();

    // Grids that arrive later: filtered collections, infinite scroll, quick view.
    if (window.MutationObserver) {
      var pending = null;
      new MutationObserver(function () {
        clearTimeout(pending);
        pending = setTimeout(function () { run(); }, 120);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { run(e.target); });

  // Small public handle, in case anything else on the page needs the count.
  window.VinodWishlist = {
    all: read,
    count: function () { return read().length; },
    has: has,
    add: function (h) { add(h); syncAll(); paintBadges(); },
    remove: function (h) { remove(h); syncAll(); paintBadges(); },
    url: PAGE
  };
})();
